import {
	type ComponentType,
	createContext,
	type ReactNode,
	useContext,
	useMemo,
} from "react";
import type {
	ApiBlockItemModel,
	BreadCrumbsResponse,
	IApiContentResponseModel,
} from "#/_generated/umbraco/delivery-api/models";

export type DocTypeRendererProps = {
	content: IApiContentResponseModel;
	breadcrumbs: BreadCrumbsResponse | undefined | null;
};

export type BlockGridItemRendererOptions = {
	isHeaderDrawer?: boolean;
	isPreview?: boolean;
};

export type BlockGridItemRendererProps = {
	model: ApiBlockItemModel;
	options?: BlockGridItemRendererOptions;
	className?: string;
};

export type DocTypeRendererMapping = Readonly<
	Record<string, ComponentType<DocTypeRendererProps>>
>;

export type BlockGridItemRendererMapping = Readonly<
	Record<string, ComponentType<BlockGridItemRendererProps>>
>;

export type UmbracoRendererMappings = {
	pages?: DocTypeRendererMapping;
	blocks?: BlockGridItemRendererMapping;
};

type RendererMappingsContextValue = {
	pages: DocTypeRendererMapping;
	blocks: BlockGridItemRendererMapping;
};

const RendererMappingsContext = createContext<RendererMappingsContextValue>({
	pages: {},
	blocks: {},
});

export type UmbracoRendererProviderProps = UmbracoRendererMappings & {
	children: ReactNode;
};

/**
 * Adds or overrides Umbraco renderers for all descendants. Nested providers
 * extend the mappings supplied by their parents.
 */
export function UmbracoRendererProvider({
	pages,
	blocks,
	children,
}: UmbracoRendererProviderProps) {
	const parentMappings = useContext(RendererMappingsContext);
	const mappings = useMemo(
		() => ({
			pages: { ...parentMappings.pages, ...pages },
			blocks: { ...parentMappings.blocks, ...blocks },
		}),
		[parentMappings, pages, blocks],
	);

	return (
		<RendererMappingsContext value={mappings}>
			{children}
		</RendererMappingsContext>
	);
}

export function useUmbracoRendererMappings() {
	return useContext(RendererMappingsContext);
}
